import { createSlice } from '@reduxjs/toolkit';
import {
    AccountEntity,
    CommentEntity,
    GroupEntity,
    ModActionLogEntity,
    PhotoEntity,
    PostEntity,
    UserReportsEntity,
} from '@interpals/api-web';

import { MODERATION_RECENT_CONTENT_LIMIT } from 'constants/limits';

import {
    clearModRecentContent,
    getAllReports,
    getModLogItemsByUid,
    getModRecentContentComments,
    getModRecentContentGroups,
    getModRecentContentPhotos,
    getModRecentContentPosts,
    getModRecentContentUsers,
    getUserReports,
    hideReports,
    MOD_LOG_LIMIT_PER_FETCH,
    removeEntityFromRecentContent,
    resetReportsFilter,
    resetReportsList,
    setReportsFilter,
    setReportsTotal,
    syncModLogItem,
} from './actions';

export type ReportStatus = 'active' | 'archived' | 'escalated';
export type ReportEntityType =
    | 'user'
    | 'wall'
    | 'photo'
    | 'message'
    | 'thread'
    | 'prompt'
    | 'entry'
    | 'comment'
    | 'group'
    | 'all content types';
export type ReportReasonCategory =
    | 'harass'
    | 'nudity'
    | 'other'
    | 'spam'
    | 'sex'
    | 'obscenity'
    | 'drugs'
    | 'all reasons';
export type ReportSortValue = 'quantity' | 'newest' | 'oldest';
export type EntityLists = {
    posts: PostEntity[];
    photos: PhotoEntity[];
    users: AccountEntity[];
    comments: CommentEntity[];
    groups: GroupEntity[];
};

export interface ModReportFilters {
    report_status?: ReportStatus;
    entity_type?: ReportEntityType;
    reason_category?: ReportReasonCategory;
    sort?: ReportSortValue;
}

export interface ModerationState {
    reports: {
        account: UserReportsEntity['account'];
        reports: UserReportsEntity['reports'];
        log?: ModActionLogEntity[];
        logHasMore?: boolean;
    }[];
    filters: ModReportFilters;
    recentContent: {
        content: EntityLists;
        hasMore: boolean;
    };
    total?: number;
}

const initialState: ModerationState = {
    reports: [],
    filters: {
        sort: 'quantity',
        report_status: 'active',
    },
    recentContent: {
        content: {
            posts: [],
            photos: [],
            users: [],
            comments: [],
            groups: [],
        },
        hasMore: true,
    },
};

const moderationSlice = createSlice({
    name: 'moderation',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getAllReports.fulfilled, (state, { payload }) => {
            state.reports = payload;
        });

        builder.addCase(getUserReports.fulfilled, (state, { payload }) => {
            const existingReportIndex = state.reports.findIndex((item) => item.account.id === payload.account.id);

            if (existingReportIndex !== -1) {
                state.reports[existingReportIndex] = { ...state.reports[existingReportIndex], ...payload };
            } else {
                state.reports.push(payload);
            }
        });

        // Add items to reports list for the given user
        builder.addCase(
            getModLogItemsByUid.fulfilled,
            (
                state,
                {
                    payload: logItems,
                    meta: {
                        arg: { userId, offsetLogId },
                    },
                },
            ) => {
                const targetUserReports = state.reports.findIndex((item) => item.account.id === userId);
                if (targetUserReports >= 0) {
                    if (offsetLogId === null) {
                        state.reports[targetUserReports].log = logItems;
                    } else {
                        state.reports[targetUserReports].log = [
                            ...(state.reports[targetUserReports].log ?? []),
                            ...logItems,
                        ];
                    }

                    state.reports[targetUserReports].logHasMore = logItems.length === MOD_LOG_LIMIT_PER_FETCH;
                }
            },
        );

        builder.addCase(setReportsFilter, (state, { payload }) => {
            state.filters = { ...state.filters, ...payload };
        });

        builder.addCase(resetReportsFilter, (state) => {
            state.filters = initialState.filters;
        });

        builder.addCase(setReportsTotal, (state, { payload }) => {
            state.total = payload;
        });

        builder.addCase(resetReportsList, (state) => {
            state.reports = [];
        });

        builder.addCase(syncModLogItem, (state, { payload: logItem }) => {
            // Update user status in reports list
            const targetReportedUser = state.reports.findIndex((user) => user.account.id === logItem.user_id);

            if (targetReportedUser >= 0) {
                // Add new log item to the top of the list if not yet present
                if (!state.reports[targetReportedUser].log?.some((item) => item.id === logItem.id)) {
                    state.reports[targetReportedUser].log = [logItem, ...(state.reports[targetReportedUser].log || [])];
                }

                // Update account status based on log item action
                if (logItem.action === 'delete' || logItem.action === 'delete-user-data') {
                    state.reports[targetReportedUser].account.status = 'deleted';
                }

                if (logItem.action === 'approve' || logItem.action === 'restore') {
                    state.reports[targetReportedUser].account.status = 'active';
                }
            }
        });

        builder.addCase(hideReports, (state, { payload }) => {
            const targetUserReports = state.reports.findIndex((user) => user.account.id === payload.user_id);
            const filteredReports = state.reports[targetUserReports]?.reports.filter(
                (report) => report.entity_id !== payload.entity_id,
            );

            if ((payload.entity_type === 'user' || filteredReports.length === 0) && !payload.isSingleUserModPage) {
                // Remove the account + reports item from the list entirely if there are no reports left
                state.reports = state.reports.filter((item) => item.account.id !== payload.user_id);
            } else if (payload.entity_type !== 'user') {
                // Remove the specific report from the list
                state.reports[targetUserReports].reports = filteredReports;
            }
        });

        builder.addCase(getModRecentContentUsers.fulfilled, (state, { payload }) => {
            state.recentContent.content.users =
                state.recentContent.content.users.length > 0
                    ? [...state.recentContent.content.users, ...payload.data]
                    : payload.data;
            state.recentContent.hasMore = payload.data.length === MODERATION_RECENT_CONTENT_LIMIT;
        });

        builder.addCase(getModRecentContentGroups.fulfilled, (state, { payload }) => {
            state.recentContent.content.groups =
                state.recentContent.content.groups.length > 0
                    ? [...state.recentContent.content.groups, ...payload.data]
                    : payload.data;
            state.recentContent.hasMore = payload.data.length === MODERATION_RECENT_CONTENT_LIMIT;
        });

        builder.addCase(getModRecentContentComments.fulfilled, (state, { payload }) => {
            state.recentContent.content.comments =
                state.recentContent.content.comments.length > 0
                    ? [...state.recentContent.content.comments, ...payload.data]
                    : payload.data;
            state.recentContent.hasMore = payload.data.length === MODERATION_RECENT_CONTENT_LIMIT;
        });

        builder.addCase(getModRecentContentPosts.fulfilled, (state, { payload }) => {
            state.recentContent.content.posts =
                state.recentContent.content.posts.length > 0
                    ? [...state.recentContent.content.posts, ...payload.data]
                    : payload.data;
            state.recentContent.hasMore = payload.data.length === MODERATION_RECENT_CONTENT_LIMIT;
        });

        builder.addCase(getModRecentContentPhotos.fulfilled, (state, { payload }) => {
            state.recentContent.hasMore = true;
            state.recentContent.content.photos =
                state.recentContent.content.photos.length > 0
                    ? [...state.recentContent.content.photos, ...payload.data]
                    : payload.data;
            state.recentContent.hasMore = payload.data.length === MODERATION_RECENT_CONTENT_LIMIT;
        });

        builder.addCase(clearModRecentContent, (state) => {
            state.recentContent.content = {
                posts: [],
                photos: [],
                users: [],
                comments: [],
                groups: [],
            };
            state.recentContent.hasMore = true;
        });

        builder.addCase(removeEntityFromRecentContent, (state: ModerationState, { payload }) => {
            switch (payload.entity_type) {
                case 'post':
                    state.recentContent.content.posts = state.recentContent.content.posts.filter(
                        (post) => post.id !== payload.entity.id,
                    );
                    break;
                case 'photo':
                    state.recentContent.content.photos = state.recentContent.content.photos.filter(
                        (photo) => photo.id !== payload.entity.id,
                    );
                    break;
                case 'user':
                    state.recentContent.content.users = state.recentContent.content.users.filter(
                        (user) => user.id !== payload.entity.id,
                    );
                    break;
                case 'comment':
                    state.recentContent.content.comments = state.recentContent.content.comments.filter(
                        (comment) => comment.id !== payload.entity.id,
                    );
                    break;
                case 'group':
                    state.recentContent.content.groups = state.recentContent.content.groups.filter(
                        (group) => group.id !== payload.entity.id,
                    );
                    break;

                default:
                    throw new Error('Unknown entity type');
            }
        });
    },
});

export default moderationSlice.reducer;
